import { create } from 'zustand';
import {
  supabase,
  DBSite,
  DBSiteEquipment,
  DBJob,
  DBJobComment,
  DBJobHistory,
  DBJobRecurrenceRule,
} from '../lib/supabase';
import {
  buildTowerWithEquipment,
  calculateMountingPoints,
  findMountingPoint,
  hydrateSiteTower,
  normalizeStayWires,
  resolveMountingPointId,
} from '../lib/towerUtils';
import {
  computeNextRunAt,
  isPastEndDate,
  isValidOccurrenceDate,
  startOfDay,
  toDateKey,
} from '../lib/jobRecurrence';
import {
  Job,
  JobComment,
  JobHistoryEntry,
  JobRecurrenceInput,
  JobRecurrenceRule,
  Site,
  StayWire,
  TowerEquipment,
  TowerShape,
  TowerSide,
} from '../types/site';
import { useProjectStore } from './projectStore';
import {
  linkOrphanedDataToCollection,
} from '../lib/collectionPersistence';
import { markOwnWrite } from '../lib/realtime/ownWrites';

const LEGACY_SITES_KEY = 'netprobuilder_sites';
const migratedProjects = new Set<string>();

const generateId = () =>
  'xxxx-xxxx-xxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16));

const MAX_RECURRENCE_CATCHUP = 36;

const parseDate = (value: string | Date | undefined): Date => {
  if (!value) return new Date();
  return value instanceof Date ? value : new Date(value);
};

const toRecurrenceRule = (row: DBJobRecurrenceRule): JobRecurrenceRule => ({
  id: row.id,
  siteId: row.site_id,
  title: row.title,
  description: row.description ?? undefined,
  priority: row.priority ?? undefined,
  assignedTo: row.assigned_to ?? undefined,
  intervalCount: row.interval_count,
  intervalUnit: row.interval_unit,
  startDate: parseDate(row.start_date),
  endDate: row.end_date ? parseDate(row.end_date) : undefined,
  nextRunAt: parseDate(row.next_run_at),
  lastRunAt: row.last_run_at ? parseDate(row.last_run_at) : undefined,
  isActive: row.is_active,
  createdAt: parseDate(row.created_at),
  updatedAt: parseDate(row.updated_at),
});

const recurrenceRuleRowFromInput = (
  id: string,
  siteId: string,
  title: string,
  description: string | undefined,
  priority: Job['priority'],
  assignedTo: string | undefined,
  input: JobRecurrenceInput,
  nextRunAt: Date,
): Omit<DBJobRecurrenceRule, 'created_at' | 'updated_at'> => ({
  id,
  site_id: siteId,
  title,
  description: description ?? null,
  priority: priority ?? null,
  assigned_to: assignedTo ?? null,
  interval_count: input.intervalCount,
  interval_unit: input.intervalUnit,
  start_date: toDateKey(input.startDate),
  end_date: input.endDate ? toDateKey(input.endDate) : null,
  next_run_at: nextRunAt.toISOString(),
  last_run_at: null,
  is_active: true,
});

const mapJobFromRow = (
  jobRow: DBJob,
  commentRows: DBJobComment[],
  historyRows: DBJobHistory[],
): Job => {
  const comments: JobComment[] = commentRows
    .filter((c) => c.job_id === jobRow.id)
    .map((c) => ({
      id: c.id,
      text: c.text,
      authorName: c.author_name,
      createdAt: parseDate(c.created_at),
    }));

  const history: JobHistoryEntry[] = historyRows
    .filter((h) => h.job_id === jobRow.id)
    .map((h) => ({
      id: h.id,
      field: h.field,
      fromValue: h.from_value ?? undefined,
      toValue: h.to_value,
      changedBy: h.changed_by,
      changedAt: parseDate(h.changed_at),
    }));

  return {
    id: jobRow.id,
    title: jobRow.title,
    description: jobRow.description ?? undefined,
    status: jobRow.status,
    priority: jobRow.priority ?? undefined,
    assignedTo: jobRow.assigned_to ?? undefined,
    dueDate: jobRow.due_date ? parseDate(jobRow.due_date) : undefined,
    recurrenceRuleId: jobRow.recurrence_rule_id ?? undefined,
    occurrenceDate: jobRow.occurrence_date ? parseDate(jobRow.occurrence_date) : undefined,
    isRecurringInstance: jobRow.is_recurring_instance ?? false,
    createdAt: parseDate(jobRow.created_at),
    updatedAt: parseDate(jobRow.updated_at),
    comments,
    history,
  };
};

const assembleSite = (
  row: DBSite,
  equipmentRows: DBSiteEquipment[],
  jobRows: DBJob[],
  commentRows: DBJobComment[],
  historyRows: DBJobHistory[]
): Site => {
  const stayWires = normalizeStayWires((row.stay_wires as StayWire[]) ?? []);
  const networkDesigns = Array.isArray(row.network_designs)
    ? (row.network_designs as string[])
    : [];

  const placements = equipmentRows
    .filter((eq) => eq.site_id === row.id)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((eq, index) => ({
      id: eq.id,
      equipment: eq.equipment as TowerEquipment['equipment'],
      mountingHeight: Number(eq.mounting_height),
      mountingSide: eq.mounting_side as TowerSide,
      azimuth: Number(eq.azimuth),
      elevation: Number(eq.elevation),
      label: eq.label ?? undefined,
      dishSize: eq.dish_size != null ? Number(eq.dish_size) : undefined,
      offset: eq.equipment_offset != null ? Number(eq.equipment_offset) : undefined,
      radio: (eq.radio as TowerEquipment['radio']) ?? undefined,
      radioConfig: (eq.radio_config as TowerEquipment['radioConfig']) ?? undefined,
      sortOrder: index,
    }));

  const { tower, equipment } = buildTowerWithEquipment(
    {
      id: row.id,
      height: Number(row.tower_height),
      crossMemberWidth: Number(row.cross_member_width),
      sectionHeight: Number(row.section_height),
      shape: row.tower_shape,
      stayWires,
      orientationDeg: Number(row.orientation_deg),
    },
    placements
  );

  const siteJobs = jobRows
    .filter((j) => j.site_id === row.id)
    .map((jobRow) => mapJobFromRow(jobRow, commentRows, historyRows));

  return {
    id: row.id,
    name: row.name,
    location: row.location,
    tower,
    equipment,
    networkDesigns,
    jobs: siteJobs,
    createdAt: parseDate(row.created_at),
    updatedAt: parseDate(row.updated_at),
  };
};

const siteRowFromSite = (collectionId: string, site: Site): DBSite => ({
  id: site.id,
  collection_id: collectionId,
  name: site.name,
  location: site.location,
  tower_height: site.tower.height,
  cross_member_width: site.tower.crossMemberWidth,
  section_height: site.tower.sectionHeight,
  tower_shape: site.tower.shape,
  orientation_deg: site.tower.orientationDeg ?? 0,
  stay_wires: site.tower.stayWires ?? [],
  network_designs: site.networkDesigns ?? [],
  created_at: site.createdAt.toISOString(),
  updated_at: site.updatedAt.toISOString(),
});

const equipmentRowsFromSite = (site: Site): Omit<DBSiteEquipment, 'created_at' | 'updated_at'>[] => {
  const hydrated = site.tower.mountingPoints?.length
    ? site
    : hydrateSiteTower(site);

  return hydrated.equipment.map((eq, index) => {
    const resolved =
      resolveMountingPointId(hydrated, eq.mountingPointId) ??
      ({ height: 0, side: 'N' as TowerSide });

    return {
      id: eq.id,
      site_id: site.id,
      equipment: eq.equipment,
      mounting_height: resolved.height,
      mounting_side: resolved.side,
      azimuth: eq.azimuth,
      elevation: eq.elevation,
      label: eq.label ?? null,
      dish_size: eq.dishSize ?? null,
      equipment_offset: eq.offset ?? null,
      radio: eq.radio ?? null,
      radio_config: eq.radioConfig ?? null,
      sort_order: index,
    };
  });
};

const jobRowFromJob = (
  job: Job,
  siteId: string | null,
  collectionId: string,
): Omit<DBJob, 'created_at' | 'updated_at'> => ({
  id: job.id,
  site_id: siteId,
  collection_id: collectionId,
  title: job.title,
  description: job.description ?? null,
  status: job.status,
  priority: job.priority ?? null,
  assigned_to: job.assignedTo ?? null,
  due_date: job.dueDate ? job.dueDate.toISOString() : null,
  recurrence_rule_id: job.recurrenceRuleId ?? null,
  occurrence_date: job.occurrenceDate ? toDateKey(job.occurrenceDate) : null,
  is_recurring_instance: job.isRecurringInstance ?? false,
});

const jobRowsFromSite = (site: Site, collectionId: string) => {
  const jobs: Omit<DBJob, 'created_at' | 'updated_at'>[] = [];
  // Comments are no longer persisted through this delete+reinsert cycle — job chat now
  // lives in the shared `chat_messages` table (see ChatPanel / chatStore). Historical
  // job_comments rows were migrated there already; this table is left frozen going forward.
  const history: Omit<DBJobHistory, 'changed_at'>[] = [];

  for (const job of site.jobs ?? []) {
    jobs.push(jobRowFromJob(job, site.id, collectionId));

    for (const entry of job.history ?? []) {
      history.push({
        id: entry.id,
        job_id: job.id,
        field: entry.field,
        from_value: entry.fromValue ?? null,
        to_value: entry.toValue,
        changed_by: entry.changedBy,
      });
    }
  }

  return { jobs, history };
};

const historyRowsFromJobs = (jobs: Job[]) => {
  const history: Omit<DBJobHistory, 'changed_at'>[] = [];
  for (const job of jobs) {
    for (const entry of job.history ?? []) {
      history.push({
        id: entry.id,
        job_id: job.id,
        field: entry.field,
        from_value: entry.fromValue ?? null,
        to_value: entry.toValue,
        changed_by: entry.changedBy,
      });
    }
  }
  return history;
};

const TEXT_SAVE_DEBOUNCE_MS = 1000;

type PendingSitePersist = {
  site: Site;
  includeJobs?: boolean;
  deleteEquipmentIds: Set<string>;
};
const pendingSitePersists = new Map<string, PendingSitePersist>();
const sitePersistTimers = new Map<string, ReturnType<typeof setTimeout>>();

type PendingJobsPersist = { jobs: Job[]; deleteIds: Set<string> };
const pendingSiteJobPersists = new Map<string, PendingJobsPersist>();
const siteJobPersistTimers = new Map<string, ReturnType<typeof setTimeout>>();
let pendingUnassigned: PendingJobsPersist | null = null;
let unassignedPersistTimer: ReturnType<typeof setTimeout> | null = null;

interface SitesState {
  sites: Site[];
  unassignedJobs: Job[];
  recurrenceRules: JobRecurrenceRule[];
  collectionId: string | null;
  isLoading: boolean;
  error: string | null;

  loadForCollection: (collectionId: string) => Promise<void>;
  getSiteById: (siteId: string) => Site | undefined;
  getJobById: (jobId: string) => Job | undefined;
  createSite: (site: Site) => Promise<void>;
  saveSite: (site: Site, options?: { includeJobs?: boolean }) => Promise<void>;
  saveSiteJobs: (siteId: string, jobs: Job[]) => Promise<void>;
  saveUnassignedJobs: (jobs: Job[]) => Promise<void>;
  applyRemoteChange: (
    table: 'sites' | 'site_equipment' | 'jobs' | 'job_recurrence_rules',
    event: 'INSERT' | 'UPDATE' | 'DELETE',
    row: Record<string, unknown> | null,
    id?: string,
  ) => void;
  createJob: (job: Job, siteId: string | null) => Promise<boolean>;
  createRecurrenceRule: (
    siteId: string,
    job: Omit<Job, 'id' | 'createdAt' | 'updatedAt'>,
    recurrence: JobRecurrenceInput,
  ) => Promise<boolean>;
  updateRecurrenceRule: (
    ruleId: string,
    job: Omit<Job, 'id' | 'createdAt' | 'updatedAt'>,
    recurrence: JobRecurrenceInput,
  ) => Promise<boolean>;
  endRecurrenceRule: (ruleId: string, lastOccurrenceDate: Date) => Promise<boolean>;
  deleteRecurrenceRule: (ruleId: string) => Promise<boolean>;
  processDueRecurringJobs: () => Promise<void>;
  deleteSite: (siteId: string) => Promise<void>;
  deleteSites: (siteIds: string[]) => Promise<void>;
  clear: () => void;
}

const fetchUnassignedJobs = async (collectionId: string) => {
  const { data: jobRows, error: jobError } = await supabase
    .from('jobs')
    .select('*')
    .eq('collection_id', collectionId)
    .is('site_id', null)
    .order('updated_at', { ascending: false });

  if (jobError) throw jobError;
  const rows = (jobRows ?? []) as DBJob[];
  if (rows.length === 0) return [] as Job[];

  const jobIds = rows.map((row) => row.id);
  const [commentRes, historyRes] = await Promise.all([
    supabase.from('job_comments').select('*').in('job_id', jobIds),
    supabase.from('job_history').select('*').in('job_id', jobIds),
  ]);
  if (commentRes.error) throw commentRes.error;
  if (historyRes.error) throw historyRes.error;

  return rows.map((row) =>
    mapJobFromRow(row, (commentRes.data ?? []) as DBJobComment[], (historyRes.data ?? []) as DBJobHistory[]),
  );
};

const fetchRecurrenceRules = async (siteIds: string[]) => {
  if (siteIds.length === 0) return [] as JobRecurrenceRule[];
  const { data, error } = await supabase
    .from('job_recurrence_rules')
    .select('*')
    .in('site_id', siteIds)
    .order('title', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as DBJobRecurrenceRule[]).map(toRecurrenceRule);
};

const deleteRecurrenceRuleRow = async (ruleId: string) => {
  const { error } = await supabase
    .from('job_recurrence_rules')
    .delete()
    .eq('id', ruleId);
  if (error) throw error;
};

const materializeRecurringJob = async (
  rule: JobRecurrenceRule,
  occurrenceDate: Date,
  collectionId: string,
): Promise<Job | null> => {
  const now = new Date().toISOString();
  const jobId = generateId();
  const occurrenceKey = toDateKey(occurrenceDate);
  const row = {
    id: jobId,
    site_id: rule.siteId,
    collection_id: collectionId,
    title: rule.title,
    description: rule.description ?? null,
    status: 'pending' as const,
    priority: rule.priority ?? null,
    assigned_to: rule.assignedTo ?? null,
    due_date: startOfDay(occurrenceDate).toISOString(),
    recurrence_rule_id: rule.id,
    occurrence_date: occurrenceKey,
    is_recurring_instance: true,
    created_at: now,
    updated_at: now,
  };

  const { error } = await supabase.from('jobs').insert(row);
  if (error) {
    if (error.code === '23505') return null;
    throw error;
  }

  return {
    id: jobId,
    title: rule.title,
    description: rule.description,
    status: 'pending',
    priority: rule.priority,
    assignedTo: rule.assignedTo,
    dueDate: startOfDay(occurrenceDate),
    recurrenceRuleId: rule.id,
    occurrenceDate: startOfDay(occurrenceDate),
    isRecurringInstance: true,
    createdAt: new Date(now),
    updatedAt: new Date(now),
    comments: [],
    history: [],
  };
};

const runRecurrenceCatchup = async (
  rules: JobRecurrenceRule[],
  sites: Site[],
  collectionId: string,
): Promise<{ rules: JobRecurrenceRule[]; sites: Site[] }> => {
  const now = new Date();
  let nextRules = [...rules];
  let nextSites = [...sites];

  for (let ruleIndex = 0; ruleIndex < nextRules.length; ruleIndex++) {
    let rule = nextRules[ruleIndex];
    if (!rule.isActive) continue;

    let catchup = 0;
    while (rule.nextRunAt <= now && catchup < MAX_RECURRENCE_CATCHUP) {
      if (isPastEndDate(rule.nextRunAt, rule.endDate)) {
        await deleteRecurrenceRuleRow(rule.id);
        nextRules = nextRules.filter((r) => r.id !== rule.id);
        rule = null as unknown as JobRecurrenceRule;
        break;
      }

      const siteIndex = nextSites.findIndex((s) => s.id === rule.siteId);
      if (siteIndex < 0) break;

      const site = nextSites[siteIndex];
      const occurrenceKey = toDateKey(rule.nextRunAt);
      const alreadyExists = (site.jobs ?? []).some(
        (j) => j.recurrenceRuleId === rule.id
          && j.occurrenceDate
          && toDateKey(j.occurrenceDate) === occurrenceKey,
      );

      if (!alreadyExists) {
        const created = await materializeRecurringJob(rule, rule.nextRunAt, collectionId);
        if (created) {
          const updatedSite = {
            ...site,
            jobs: [...(site.jobs ?? []), created],
            updatedAt: new Date(),
          };
          nextSites = nextSites.map((s, i) => (i === siteIndex ? updatedSite : s));
        }
      }

      const lastRunAt = rule.nextRunAt;
      const nextRunAt = computeNextRunAt(
        rule.nextRunAt,
        rule.intervalCount,
        rule.intervalUnit,
      );
      const shouldDeactivate = isPastEndDate(nextRunAt, rule.endDate);
      const updatedAt = new Date().toISOString();

      if (shouldDeactivate) {
        await deleteRecurrenceRuleRow(rule.id);
        nextRules = nextRules.filter((r) => r.id !== rule.id);
        rule = null as unknown as JobRecurrenceRule;
        break;
      }

      const { error } = await supabase
        .from('job_recurrence_rules')
        .update({
          next_run_at: nextRunAt.toISOString(),
          last_run_at: lastRunAt.toISOString(),
          is_active: true,
          updated_at: updatedAt,
        })
        .eq('id', rule.id);
      if (error) throw error;

      rule = {
        ...rule,
        nextRunAt,
        lastRunAt,
        isActive: true,
        updatedAt: new Date(updatedAt),
      };
      catchup++;
    }

    if (rule) {
      nextRules[ruleIndex] = rule;
    }
  }

  return { rules: nextRules, sites: nextSites };
};

const fetchRelated = async (siteIds: string[]) => {
  if (siteIds.length === 0) {
    return {
      equipmentRows: [] as DBSiteEquipment[],
      jobRows: [] as DBJob[],
      commentRows: [] as DBJobComment[],
      historyRows: [] as DBJobHistory[],
    };
  }

  const [equipmentRes, jobsRes] = await Promise.all([
    supabase.from('site_equipment').select('*').in('site_id', siteIds),
    supabase.from('jobs').select('*').in('site_id', siteIds),
  ]);

  if (equipmentRes.error) throw equipmentRes.error;
  if (jobsRes.error) throw jobsRes.error;

  const jobIds = (jobsRes.data ?? []).map((j) => j.id);
  if (jobIds.length === 0) {
    return {
      equipmentRows: (equipmentRes.data ?? []) as DBSiteEquipment[],
      jobRows: [] as DBJob[],
      commentRows: [] as DBJobComment[],
      historyRows: [] as DBJobHistory[],
    };
  }

  const [commentsRes, historyRes] = await Promise.all([
    supabase.from('job_comments').select('*').in('job_id', jobIds),
    supabase.from('job_history').select('*').in('job_id', jobIds),
  ]);

  if (commentsRes.error) throw commentsRes.error;
  if (historyRes.error) throw historyRes.error;

  return {
    equipmentRows: (equipmentRes.data ?? []) as DBSiteEquipment[],
    jobRows: (jobsRes.data ?? []) as DBJob[],
    commentRows: (commentsRes.data ?? []) as DBJobComment[],
    historyRows: (historyRes.data ?? []) as DBJobHistory[],
  };
};

const migrateLegacySites = async (collectionId: string, legacySites: Site[]) => {
  for (const site of legacySites) {
    const normalized = hydrateSiteTower({
      ...site,
      tower: {
        ...site.tower,
        shape: (site.tower.shape ?? 'square') as TowerShape,
        sectionHeight: site.tower.sectionHeight ?? 3,
        stayWires: site.tower.stayWires ?? [],
        orientationDeg: site.tower.orientationDeg ?? 0,
        mountingPoints:
          site.tower.mountingPoints?.length > 0
            ? site.tower.mountingPoints
            : calculateMountingPoints(site.tower.height, site.tower.shape ?? 'square'),
      },
      createdAt: parseDate(site.createdAt),
      updatedAt: parseDate(site.updatedAt),
    });

    await useSitesStore.getState().createSite(normalized);
  }
};

export const useSitesStore = create<SitesState>((set, get) => ({
  sites: [],
  unassignedJobs: [],
  recurrenceRules: [],
  collectionId: null,
  isLoading: false,
  error: null,

  loadForCollection: async (collectionId: string) => {
    set({ isLoading: true, error: null, collectionId });

    try {
      await linkOrphanedDataToCollection(collectionId);

      const { data: siteRows, error: siteError } = await supabase
        .from('sites')
        .select('*')
        .eq('collection_id', collectionId)
        .order('name', { ascending: true });

      if (siteError) throw siteError;

      const rows = (siteRows ?? []) as DBSite[];

      if (rows.length === 0 && !migratedProjects.has(collectionId)) {
        const collection = useProjectStore.getState().currentProject;
        const legacyFromProject = (collection?.sites as Site[] | undefined) ?? [];

        let legacyFromStorage: Site[] = [];
        try {
          const raw = localStorage.getItem(LEGACY_SITES_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            const values = Array.isArray(parsed) ? parsed : Object.values(parsed ?? {});
            legacyFromStorage = (values as Site[]).map((s) => ({
              ...s,
              createdAt: parseDate(s.createdAt),
              updatedAt: parseDate(s.updatedAt),
            }));
          }
        } catch {
          legacyFromStorage = [];
        }

        const legacyMap = new Map<string, Site>();
        legacyFromStorage.forEach((s) => s?.id && legacyMap.set(s.id, s));
        legacyFromProject.forEach((s) => s?.id && legacyMap.set(s.id, s));
        const legacySites = Array.from(legacyMap.values());

        if (legacySites.length > 0) {
          await migrateLegacySites(collectionId, legacySites);
          migratedProjects.add(collectionId);
          return get().loadForCollection(collectionId);
        }

        migratedProjects.add(collectionId);
      }

      const siteIds = rows.map((r) => r.id);
      const related = await fetchRelated(siteIds);
      let sites = rows.map((row) =>
        assembleSite(row, related.equipmentRows, related.jobRows, related.commentRows, related.historyRows)
      );

      let recurrenceRules: JobRecurrenceRule[] = [];
      let unassignedJobs: Job[] = [];
      try {
        unassignedJobs = await fetchUnassignedJobs(collectionId);
      } catch (unassignedErr) {
        console.warn('Failed to load unassigned jobs:', unassignedErr);
      }

      try {
        recurrenceRules = await fetchRecurrenceRules(siteIds);
        const catchup = await runRecurrenceCatchup(recurrenceRules, sites, collectionId);
        recurrenceRules = catchup.rules;
        sites = catchup.sites;
      } catch (recurrenceErr) {
        console.warn('Recurrence features unavailable; loaded sites without schedules:', recurrenceErr);
      }

      set({ sites, unassignedJobs, recurrenceRules, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load sites';
      console.error('Failed to load sites:', err);
      set({ isLoading: false, error: message });
    }
  },

  getSiteById: (siteId: string) => get().sites.find((s) => s.id === siteId),

  getJobById: (jobId: string) => {
    for (const site of get().sites) {
      const job = site.jobs?.find((j) => j.id === jobId);
      if (job) return job;
    }
    return get().unassignedJobs.find((j) => j.id === jobId);
  },

  createSite: async (site: Site) => {
    const collectionId = get().collectionId;
    if (!collectionId) throw new Error('No collection loaded');

    const now = new Date();
    const siteWithDates: Site = {
      ...hydrateSiteTower(site),
      createdAt: site.createdAt ?? now,
      updatedAt: now,
    };

    const row = siteRowFromSite(collectionId, siteWithDates);
    const { error: siteError } = await supabase.from('sites').insert(row);
    if (siteError) throw siteError;

    const equipmentRows = equipmentRowsFromSite(siteWithDates).map((eq) => ({
      ...eq,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
    }));

    if (equipmentRows.length > 0) {
      const { error } = await supabase.from('site_equipment').insert(equipmentRows);
      if (error) throw error;
      equipmentRows.forEach((eq) => markOwnWrite('site_equipment', eq.id));
    }
    markOwnWrite('sites', siteWithDates.id);

    const { jobs, history } = jobRowsFromSite(siteWithDates, collectionId);
    if (jobs.length > 0) {
      const nowIso = now.toISOString();
      const { error: jobsError } = await supabase.from('jobs').insert(
        jobs.map((j) => ({ ...j, created_at: nowIso, updated_at: nowIso }))
      );
      if (jobsError) throw jobsError;
      jobs.forEach((j) => markOwnWrite('jobs', j.id));

      if (history.length > 0) {
        const { error } = await supabase.from('job_history').insert(
          history.map((h) => ({ ...h, changed_at: nowIso }))
        );
        if (error) throw error;
      }
    }

    set((state) => ({
      sites: [...state.sites.filter((s) => s.id !== siteWithDates.id), siteWithDates].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    }));
  },

  saveSite: async (site: Site, options?: { includeJobs?: boolean }) => {
    const collectionId = get().collectionId;
    if (!collectionId) throw new Error('No collection loaded');

    const now = new Date();
    const updatedSite: Site = {
      ...hydrateSiteTower(site),
      updatedAt: now,
    };

    const previousIds = new Set((get().getSiteById(site.id)?.equipment ?? []).map((eq) => eq.id));
    const incomingIds = new Set(updatedSite.equipment.map((eq) => eq.id));
    const deleteEquipmentIds = Array.from(previousIds).filter((id) => !incomingIds.has(id));

    set((state) => ({
      sites: state.sites.map((s) => (s.id === updatedSite.id ? updatedSite : s)),
    }));

    const pending = pendingSitePersists.get(site.id) ?? {
      site: updatedSite,
      includeJobs: options?.includeJobs,
      deleteEquipmentIds: new Set<string>(),
    };
    pending.site = updatedSite;
    pending.includeJobs = options?.includeJobs ?? pending.includeJobs;
    deleteEquipmentIds.forEach((id) => pending.deleteEquipmentIds.add(id));
    pendingSitePersists.set(site.id, pending);

    const prevTimer = sitePersistTimers.get(site.id);
    if (prevTimer) clearTimeout(prevTimer);

    await new Promise<void>((resolve, reject) => {
      sitePersistTimers.set(
        site.id,
        setTimeout(() => {
          const queued = pendingSitePersists.get(site.id);
          pendingSitePersists.delete(site.id);
          sitePersistTimers.delete(site.id);
          if (!queued) {
            resolve();
            return;
          }
          void (async () => {
            try {
              const row = siteRowFromSite(collectionId, queued.site);
              const { error: siteError } = await supabase.from('sites').upsert(row);
              if (siteError) throw siteError;
              markOwnWrite('sites', queued.site.id);

              const toDelete = Array.from(queued.deleteEquipmentIds);
              if (toDelete.length > 0) {
                const { error: deleteError } = await supabase.from('site_equipment').delete().in('id', toDelete);
                if (deleteError) throw deleteError;
              }

              const equipmentRows = equipmentRowsFromSite(queued.site).map((eq) => ({
                ...eq,
                created_at: now.toISOString(),
                updated_at: now.toISOString(),
              }));
              if (equipmentRows.length > 0) {
                const { error } = await supabase
                  .from('site_equipment')
                  .upsert(equipmentRows, { onConflict: 'id' });
                if (error) throw error;
                equipmentRows.forEach((eq) => markOwnWrite('site_equipment', eq.id));
              }

              if (queued.includeJobs) {
                await get().saveSiteJobs(queued.site.id, queued.site.jobs ?? []);
              }
              resolve();
            } catch (err) {
              reject(err);
            }
          })();
        }, TEXT_SAVE_DEBOUNCE_MS)
      );
    });
  },

  saveSiteJobs: async (siteId: string, jobs: Job[]) => {
    const collectionId = get().collectionId;
    if (!collectionId) throw new Error('No collection loaded');

    const now = new Date().toISOString();
    const previousLocalIds = new Set((get().getSiteById(siteId)?.jobs ?? []).map((j) => j.id));
    const incomingIds = new Set(jobs.map((j) => j.id));
    const toDeleteNow = Array.from(previousLocalIds).filter((id) => !incomingIds.has(id));

    set((state) => ({
      sites: state.sites.map((s) =>
        s.id === siteId ? { ...s, jobs, updatedAt: new Date(now) } : s
      ),
    }));

    const pending = pendingSiteJobPersists.get(siteId) ?? { jobs, deleteIds: new Set<string>() };
    pending.jobs = jobs;
    toDeleteNow.forEach((id) => pending.deleteIds.add(id));
    pendingSiteJobPersists.set(siteId, pending);

    const prevTimer = siteJobPersistTimers.get(siteId);
    if (prevTimer) clearTimeout(prevTimer);

    await new Promise<void>((resolve, reject) => {
      siteJobPersistTimers.set(
        siteId,
        setTimeout(() => {
          const queued = pendingSiteJobPersists.get(siteId);
          pendingSiteJobPersists.delete(siteId);
          siteJobPersistTimers.delete(siteId);
          if (!queued) {
            resolve();
            return;
          }
          void (async () => {
            try {
              const toDelete = Array.from(queued.deleteIds);
              if (toDelete.length > 0) {
                const { error: deleteError } = await supabase.from('jobs').delete().in('id', toDelete);
                if (deleteError) throw deleteError;
              }

              const site = get().getSiteById(siteId);
              const siteSnapshot: Site = site
                ? { ...site, jobs: queued.jobs }
                : {
                    id: siteId,
                    name: '',
                    location: '',
                    tower: {
                      id: siteId,
                      height: 30,
                      crossMemberWidth: 450,
                      sectionHeight: 3,
                      shape: 'square',
                      mountingPoints: [],
                      stayWires: [],
                    },
                    equipment: [],
                    jobs: queued.jobs,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  };

              const { jobs: jobRows, history } = jobRowsFromSite(siteSnapshot, collectionId);
              if (jobRows.length > 0) {
                const { error: jobsError } = await supabase.from('jobs').upsert(
                  jobRows.map((j) => {
                    const existing = queued.jobs.find((job) => job.id === j.id);
                    return {
                      ...j,
                      created_at: existing?.createdAt?.toISOString?.() ?? now,
                      updated_at: now,
                    };
                  }),
                  { onConflict: 'id' },
                );
                if (jobsError) throw jobsError;
                jobRows.forEach((j) => markOwnWrite('jobs', j.id));
              }

              if (history.length > 0) {
                const { error } = await supabase.from('job_history').upsert(
                  history.map((h) => ({ ...h, changed_at: now })),
                  { onConflict: 'id' },
                );
                if (error) throw error;
              }
              resolve();
            } catch (err) {
              reject(err);
            }
          })();
        }, TEXT_SAVE_DEBOUNCE_MS)
      );
    });
  },

  saveUnassignedJobs: async (jobs: Job[]) => {
    const collectionId = get().collectionId;
    if (!collectionId) throw new Error('No collection loaded');

    const now = new Date().toISOString();
    const previousLocalIds = new Set(get().unassignedJobs.map((j) => j.id));
    const incomingIds = new Set(jobs.map((j) => j.id));
    const toDeleteNow = Array.from(previousLocalIds).filter((id) => !incomingIds.has(id));

    set({ unassignedJobs: jobs });

    pendingUnassigned = pendingUnassigned ?? { jobs, deleteIds: new Set<string>() };
    pendingUnassigned.jobs = jobs;
    toDeleteNow.forEach((id) => pendingUnassigned!.deleteIds.add(id));

    if (unassignedPersistTimer) clearTimeout(unassignedPersistTimer);

    await new Promise<void>((resolve, reject) => {
      unassignedPersistTimer = setTimeout(() => {
        const queued = pendingUnassigned;
        pendingUnassigned = null;
        unassignedPersistTimer = null;
        if (!queued) {
          resolve();
          return;
        }
        void (async () => {
          try {
            const toDelete = Array.from(queued.deleteIds);
            if (toDelete.length > 0) {
              const { error: deleteError } = await supabase.from('jobs').delete().in('id', toDelete);
              if (deleteError) throw deleteError;
            }

            const jobRows = queued.jobs.map((job) => jobRowFromJob(job, null, collectionId));
            const history = historyRowsFromJobs(queued.jobs);

            if (jobRows.length > 0) {
              const { error: jobsError } = await supabase.from('jobs').upsert(
                jobRows.map((j) => {
                  const existing = queued.jobs.find((job) => job.id === j.id);
                  return {
                    ...j,
                    created_at: existing?.createdAt?.toISOString?.() ?? now,
                    updated_at: now,
                  };
                }),
                { onConflict: 'id' },
              );
              if (jobsError) throw jobsError;
              jobRows.forEach((j) => markOwnWrite('jobs', j.id));
            }

            if (history.length > 0) {
              const { error } = await supabase.from('job_history').upsert(
                history.map((h) => ({ ...h, changed_at: now })),
                { onConflict: 'id' },
              );
              if (error) throw error;
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        })();
      }, TEXT_SAVE_DEBOUNCE_MS);
    });
  },

  createJob: async (job: Job, siteId: string | null) => {
    try {
      if (siteId) {
        const site = get().getSiteById(siteId);
        if (!site) return false;
        await get().saveSiteJobs(siteId, [...(site.jobs ?? []), job]);
      } else {
        await get().saveUnassignedJobs([...get().unassignedJobs, job]);
      }
      return true;
    } catch (err) {
      console.error('Failed to create job:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to create job' });
      return false;
    }
  },

  createRecurrenceRule: async (siteId, job, recurrence) => {
    try {
      const id = generateId();
      const nextRunAt = startOfDay(recurrence.startDate);
      const now = new Date().toISOString();
      const row = {
        ...recurrenceRuleRowFromInput(
          id,
          siteId,
          job.title,
          job.description,
          job.priority,
          job.assignedTo,
          recurrence,
          nextRunAt,
        ),
        created_at: now,
        updated_at: now,
      };

      const { error } = await supabase.from('job_recurrence_rules').insert(row);
      if (error) throw error;

      const rule = toRecurrenceRule(row as DBJobRecurrenceRule);
      set((state) => ({
        recurrenceRules: [...state.recurrenceRules, rule].sort((a, b) => a.title.localeCompare(b.title)),
      }));

      await get().processDueRecurringJobs();
      return true;
    } catch (err) {
      console.error('Failed to create recurrence rule:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to create recurring job' });
      return false;
    }
  },

  updateRecurrenceRule: async (ruleId, job, recurrence) => {
    try {
      const existing = get().recurrenceRules.find((r) => r.id === ruleId);
      if (!existing) return false;

      const nextRunAt = startOfDay(recurrence.startDate);
      const now = new Date().toISOString();
      const updates = {
        title: job.title,
        description: job.description ?? null,
        priority: job.priority ?? null,
        assigned_to: job.assignedTo ?? null,
        interval_count: recurrence.intervalCount,
        interval_unit: recurrence.intervalUnit,
        start_date: toDateKey(recurrence.startDate),
        end_date: recurrence.endDate ? toDateKey(recurrence.endDate) : null,
        next_run_at: nextRunAt.toISOString(),
        is_active: true,
        updated_at: now,
      };

      const { error } = await supabase
        .from('job_recurrence_rules')
        .update(updates)
        .eq('id', ruleId);
      if (error) throw error;

      set((state) => ({
        recurrenceRules: state.recurrenceRules
          .map((r) => r.id === ruleId
            ? {
                ...r,
                title: job.title,
                description: job.description,
                priority: job.priority,
                assignedTo: job.assignedTo,
                intervalCount: recurrence.intervalCount,
                intervalUnit: recurrence.intervalUnit,
                startDate: startOfDay(recurrence.startDate),
                endDate: recurrence.endDate ? startOfDay(recurrence.endDate) : undefined,
                nextRunAt,
                isActive: true,
                updatedAt: new Date(now),
              }
            : r)
          .sort((a, b) => a.title.localeCompare(b.title)),
      }));

      await get().processDueRecurringJobs();
      return true;
    } catch (err) {
      console.error('Failed to update recurrence rule:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to update recurring job' });
      return false;
    }
  },

  deleteRecurrenceRule: async (ruleId) => {
    try {
      await deleteRecurrenceRuleRow(ruleId);

      set((state) => ({
        recurrenceRules: state.recurrenceRules.filter((r) => r.id !== ruleId),
      }));
      return true;
    } catch (err) {
      console.error('Failed to delete recurrence rule:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to delete recurring job' });
      return false;
    }
  },

  endRecurrenceRule: async (ruleId, lastOccurrenceDate) => {
    try {
      const rule = get().recurrenceRules.find((r) => r.id === ruleId);
      if (!rule) return false;

      const endDate = startOfDay(lastOccurrenceDate);
      if (endDate.getTime() < startOfDay(rule.startDate).getTime()) {
        set({ error: 'End date cannot be before the schedule start.' });
        return false;
      }
      if (!isValidOccurrenceDate(rule.startDate, rule.intervalCount, rule.intervalUnit, endDate)) {
        set({ error: 'Choose a date that matches the recurring schedule.' });
        return false;
      }

      const now = new Date().toISOString();
      const { error: updateErr } = await supabase
        .from('job_recurrence_rules')
        .update({
          end_date: toDateKey(endDate),
          updated_at: now,
        })
        .eq('id', ruleId);
      if (updateErr) throw updateErr;

      let updatedRule: JobRecurrenceRule = {
        ...rule,
        endDate,
        updatedAt: new Date(now),
      };
      set((state) => ({
        recurrenceRules: state.recurrenceRules.map((r) => (r.id === ruleId ? updatedRule : r)),
      }));

      const today = startOfDay(new Date());
      if (endDate.getTime() <= today.getTime()) {
        const siteIndex = get().sites.findIndex((s) => s.id === rule.siteId);
        if (siteIndex >= 0) {
          const site = get().sites[siteIndex];
          const occurrenceKey = toDateKey(endDate);
          const exists = (site.jobs ?? []).some(
            (j) => j.recurrenceRuleId === ruleId
              && j.occurrenceDate
              && toDateKey(j.occurrenceDate) === occurrenceKey,
          );
          if (!exists) {
            const created = await materializeRecurringJob(updatedRule, endDate, get().collectionId ?? '');
            if (created) {
              set((state) => ({
                sites: state.sites.map((s, i) => (i === siteIndex
                  ? { ...s, jobs: [...(s.jobs ?? []), created], updatedAt: new Date() }
                  : s)),
              }));
            }
          }
        }
      }

      await get().processDueRecurringJobs();

      const remaining = get().recurrenceRules.find((r) => r.id === ruleId);
      if (remaining && (endDate.getTime() <= today.getTime() || isPastEndDate(remaining.nextRunAt, endDate))) {
        await get().deleteRecurrenceRule(ruleId);
      }

      return true;
    } catch (err) {
      console.error('Failed to end recurrence rule:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to end recurring job' });
      return false;
    }
  },

  processDueRecurringJobs: async () => {
    const { recurrenceRules, sites } = get();
    if (recurrenceRules.length === 0) return;
    try {
      const catchup = await runRecurrenceCatchup(recurrenceRules, sites, get().collectionId ?? '');
      set({ recurrenceRules: catchup.rules, sites: catchup.sites });
    } catch (err) {
      console.error('Failed to process recurring jobs:', err);
    }
  },

  deleteSite: async (siteId: string) => {
    const { error } = await supabase.from('sites').delete().eq('id', siteId);
    if (error) throw error;
    set((state) => ({ sites: state.sites.filter((s) => s.id !== siteId) }));
  },

  deleteSites: async (siteIds: string[]) => {
    if (siteIds.length === 0) return;
    const { error } = await supabase.from('sites').delete().in('id', siteIds);
    if (error) throw error;
    const idSet = new Set(siteIds);
    set((state) => ({ sites: state.sites.filter((s) => !idSet.has(s.id)) }));
  },

  applyRemoteChange: (table, event, row, id) => {
    const collectionId = get().collectionId;
    const rowId = id ?? (row?.id as string | undefined);
    if (!rowId) return;

    if (table === 'sites') {
      if (row?.collection_id && collectionId && row.collection_id !== collectionId) return;
      if (event === 'DELETE') {
        set((state) => ({ sites: state.sites.filter((s) => s.id !== rowId) }));
        return;
      }
      const dbRow = row as unknown as DBSite;
      if (!dbRow.name || !dbRow.id) {
        void get().loadForCollection(collectionId ?? '');
        return;
      }
      const existing = get().getSiteById(rowId);
      const assembled = assembleSite(dbRow, [], [], [], []);
      assembled.jobs = existing?.jobs ?? [];
      if (existing) {
        assembled.equipment = existing.equipment;
        assembled.tower = {
          ...assembled.tower,
          mountingPoints: existing.tower.mountingPoints,
        };
      }
      set((state) => ({
        sites: [...state.sites.filter((s) => s.id !== rowId), assembled].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      }));
      return;
    }

    if (table === 'site_equipment') {
      const siteId = (row?.site_id as string | undefined) ?? '';
      const site = siteId ? get().getSiteById(siteId) : get().sites.find((s) => s.equipment.some((eq) => eq.id === rowId));
      if (!site) return;
      if (event === 'DELETE') {
        set((state) => ({
          sites: state.sites.map((s) =>
            s.id === site.id ? { ...s, equipment: s.equipment.filter((eq) => eq.id !== rowId) } : s
          ),
        }));
        return;
      }
      void (async () => {
        try {
          const related = await fetchRelated([site.id]);
          const { data: siteRow } = await supabase.from('sites').select('*').eq('id', site.id).maybeSingle();
          if (!siteRow) return;
          const next = assembleSite(
            siteRow as DBSite,
            related.equipmentRows,
            related.jobRows,
            related.commentRows,
            related.historyRows,
          );
          next.jobs = get().getSiteById(site.id)?.jobs ?? next.jobs;
          set((state) => ({
            sites: state.sites.map((s) => (s.id === site.id ? next : s)),
          }));
        } catch (err) {
          console.warn('[realtime] site_equipment refresh failed', err);
        }
      })();
      return;
    }

    if (table === 'jobs') {
      if (row?.collection_id && collectionId && row.collection_id !== collectionId) return;
      if (event === 'DELETE') {
        set((state) => ({
          sites: state.sites.map((s) => ({
            ...s,
            jobs: (s.jobs ?? []).filter((j) => j.id !== rowId),
          })),
          unassignedJobs: state.unassignedJobs.filter((j) => j.id !== rowId),
        }));
        return;
      }
      if (!row || typeof row.title !== 'string') return;
      const mapped = mapJobFromRow(row as unknown as DBJob, [], []);
      const siteId = (row.site_id as string | null) ?? null;
      set((state) => {
        if (!siteId) {
          const existing = state.unassignedJobs.find((j) => j.id === mapped.id);
          const nextJob = existing ? { ...mapped, comments: existing.comments, history: existing.history } : mapped;
          return {
            unassignedJobs: [...state.unassignedJobs.filter((j) => j.id !== mapped.id), nextJob],
          };
        }
        return {
          sites: state.sites.map((s) => {
            if (s.id !== siteId) {
              return { ...s, jobs: (s.jobs ?? []).filter((j) => j.id !== mapped.id) };
            }
            const existing = (s.jobs ?? []).find((j) => j.id === mapped.id);
            const nextJob = existing ? { ...mapped, comments: existing.comments, history: existing.history } : mapped;
            return {
              ...s,
              jobs: [...(s.jobs ?? []).filter((j) => j.id !== mapped.id), nextJob],
            };
          }),
          unassignedJobs: state.unassignedJobs.filter((j) => j.id !== mapped.id),
        };
      });
      return;
    }

    if (table === 'job_recurrence_rules') {
      const siteId = row?.site_id as string | undefined;
      if (siteId && !get().sites.some((s) => s.id === siteId) && event !== 'DELETE') return;
      if (event === 'DELETE') {
        set((state) => ({
          recurrenceRules: state.recurrenceRules.filter((r) => r.id !== rowId),
        }));
        return;
      }
      if (!row || typeof row.title !== 'string') return;
      const rule = toRecurrenceRule(row as unknown as DBJobRecurrenceRule);
      set((state) => ({
        recurrenceRules: [...state.recurrenceRules.filter((r) => r.id !== rule.id), rule].sort((a, b) =>
          a.title.localeCompare(b.title)
        ),
      }));
    }
  },

  clear: () => set({ sites: [], unassignedJobs: [], recurrenceRules: [], collectionId: null, isLoading: false, error: null }),
}));

export { generateId as generateSiteId };
